"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { ReadOnlyChatMessages } from "@/components/playground/chat-ui";
import { ForkChatButton } from "@/components/playground/fork-chat-button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useOrgShare, useOrgShares } from "@/hooks/useChats";
import { OrgHeader } from "./org-header";
import { OrgSidebar } from "./org-sidebar";
import type { Organization } from "@/lib/types";
import type { UIMessage } from "ai";
interface SharedMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string | null;
  images: string | null;
  audios?: string | null;
  reasoning: string | null;
  tools: string | null;
  metadata?: unknown;
  sequence: number;
  createdAt: string;
}
function toUiMessage(message: SharedMessage): UIMessage {
  const parts: UIMessage["parts"] = [];
  if (message.content) {
    parts.push({
      type: "text",
      text: message.content
    });
  }
  if (message.reasoning) {
    parts.push({
      type: "reasoning",
      text: message.reasoning
    });
  }
  return {
    ...message,
    role: message.role === "system" ? "assistant" : message.role,
    parts: parts as UIMessage["parts"]
  };
}
interface OrgPageClientProps {
  organizationId: string;
  shareId: string | null;
  organizations: Organization[];
  selectedOrganization: Organization | null;
}
export default function OrgPageClient(t0) {
  const $ = _c(23);
  const {
    organizationId,
    shareId,
    organizations,
    selectedOrganization
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== router) {
    t1 = org => {
      if (org?.id) {
        router.push(`/org/${org.id}`);
        return;
      }
      router.push("/");
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelectOrganization = t1;
  const t2 = shareId ?? undefined;
  let t3;
  if ($[2] !== handleSelectOrganization || $[3] !== organizationId || $[4] !== organizations || $[5] !== selectedOrganization || $[6] !== t2) {
    t3 = <OrgSidebar organizationId={organizationId} currentShareId={t2} organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={handleSelectOrganization} />;
    $[2] = handleSelectOrganization;
    $[3] = organizationId;
    $[4] = organizations;
    $[5] = selectedOrganization;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== organizationId || $[9] !== organizations) {
    t4 = organizations.find(o => o.id === organizationId)?.name;
    $[8] = organizationId;
    $[9] = organizations;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <OrgHeader organizationName={t4} />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== organizationId || $[14] !== organizations || $[15] !== shareId) {
    t6 = <main className="flex flex-1 min-h-0 overflow-hidden"><OrgSharedChatsPanel organizationId={organizationId} shareId={shareId} organizations={organizations} /></main>;
    $[13] = organizationId;
    $[14] = organizations;
    $[15] = shareId;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className="flex h-svh bg-background w-full overflow-hidden flex-col">{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t7) {
    t8 = <SidebarProvider>{t3}{t7}</SidebarProvider>;
    $[20] = t3;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function OrgSharedChatsPanel(t0) {
  const $ = _c(42);
  const {
    organizationId,
    shareId,
    organizations
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== organizationId || $[1] !== organizations) {
    let t2;
    if ($[3] !== organizationId) {
      t2 = org => org.id === organizationId;
      $[3] = organizationId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = organizations.find(t2);
    $[0] = organizationId;
    $[1] = organizations;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const organization = t1;
  const {
    data: orgSharesData,
    isLoading: isOrgSharesLoading
  } = useOrgShares(organizationId);
  const {
    data: orgShareData,
    isLoading: isOrgShareLoading
  } = useOrgShare(shareId);
  let t2;
  if ($[5] !== orgSharesData?.shares) {
    t2 = orgSharesData?.shares ?? [];
    $[5] = orgSharesData?.shares;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const shares = t2;
  let t3;
  if ($[7] !== organizationId || $[8] !== router) {
    t3 = nextShareId => {
      router.push(`/org/${organizationId}/chat/${nextShareId}`);
    };
    $[7] = organizationId;
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const openShare = t3;
  if (!organization) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex h-full items-center justify-center px-6 text-center w-full"><p className="text-muted-foreground text-sm">Select an organization from the sidebar to view shared chats.</p></div>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (shareId) {
    const share = orgShareData?.share;
    const isOrgShareDetailLoading = isOrgShareLoading || isOrgSharesLoading;
    let t4;
    if ($[11] !== shareId) {
      t4 = item => item.id === shareId;
      $[11] = shareId;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const shareBelongsToOrganization = shares.some(t4);
    const visibleShare = !isOrgShareDetailLoading && shareBelongsToOrganization ? share : null;
    let t5;
    if ($[13] !== visibleShare) {
      t5 = visibleShare ? visibleShare.messages.map(_temp) : [];
      $[13] = visibleShare;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    const messages = t5;
    let t6;
    if ($[15] !== organization || $[16] !== visibleShare) {
      t6 = visibleShare ? <><h2 className="mt-4 text-2xl font-semibold tracking-normal">{visibleShare.title}</h2><div className="text-muted-foreground mt-2 flex flex-wrap gap-x-2 gap-y-1 text-sm"><span>{organization.name}</span><span>·</span><span>{visibleShare.model}</span><span>·</span><span>Shared{" "}{new Intl.DateTimeFormat("en", {
              dateStyle: "medium",
              timeStyle: "short"
            }).format(new Date(visibleShare.createdAt))}</span></div></> : null;
      $[15] = organization;
      $[16] = visibleShare;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t6) {
      t7 = <div className="border-b px-6 py-4">{t6}</div>;
      $[18] = t6;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== isOrgShareDetailLoading || $[21] !== messages || $[22] !== visibleShare) {
      t8 = <div className="min-h-0 flex-1 pb-20">{isOrgShareDetailLoading ? <div className="text-muted-foreground flex h-full items-center justify-center text-sm">Loading shared chat...</div> : visibleShare ? <ReadOnlyChatMessages messages={messages} /> : <div className="text-muted-foreground flex h-full items-center justify-center text-sm">Shared chat not found.</div>}</div>;
      $[20] = isOrgShareDetailLoading;
      $[21] = messages;
      $[22] = visibleShare;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== visibleShare) {
      t9 = visibleShare ? <ForkChatButton shareId={visibleShare.id} contained={true} /> : null;
      $[24] = visibleShare;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
      t10 = <div className="flex h-full min-h-0 flex-col w-full">{t7}{t8}{t9}</div>;
      $[26] = t7;
      $[27] = t8;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    return t10;
  }
  let t4;
  if ($[30] !== organization.name) {
    t4 = <h2 className="text-2xl font-semibold tracking-tight">{organization.name} chats</h2>;
    $[30] = organization.name;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-muted-foreground mt-1 text-sm">Chats shared with this organization. Open a snapshot or fork it to continue privately.</p>;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== t4) {
    t6 = <header className="pb-6">{t4}{t5}</header>;
    $[33] = t4;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== isOrgSharesLoading || $[36] !== openShare || $[37] !== shares) {
    t7 = isOrgSharesLoading ? <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">Loading organization chats...</div> : shares.length === 0 ? <div className="text-muted-foreground rounded-lg border border-dashed p-12 text-center text-sm">No chats have been shared with this organization yet.</div> : <ul className="divide-border overflow-hidden rounded-lg border">{shares.map(share_0 => <li key={share_0.id} className="border-b last:border-b-0"><button type="button" className="hover:bg-muted/50 flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors" onClick={() => openShare(share_0.id)}><span className="min-w-0 flex-1"><span className="block truncate font-medium">{share_0.title}</span><span className="text-muted-foreground mt-0.5 block truncate text-xs">{share_0.model}</span></span><span className="text-muted-foreground shrink-0 text-xs">{new Intl.DateTimeFormat("en", {
              month: "short",
              day: "numeric",
              year: "numeric"
            }).format(new Date(share_0.updatedAt))}</span></button></li>)}</ul>;
    $[35] = isOrgSharesLoading;
    $[36] = openShare;
    $[37] = shares;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t6 || $[40] !== t7) {
    t8 = <div className="mx-auto flex h-full w-full max-w-3xl flex-col px-6 py-8">{t6}{t7}</div>;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  return t8;
}
function _temp(message) {
  return toUiMessage(message as SharedMessage);
}